export type DateUnit = 'date' | 'week' | 'month' | 'year' | 'quarter';
export interface DateLimits { min?: string; max?: string; disabledDate?: (day: string) => boolean }
const pad = (n: number, length = 2) => String(n).padStart(length, '0');
export function dateAt(year: number, month: number, day = 1): Date {
  const result = new Date(0); result.setUTCFullYear(year, month - 1, day); result.setUTCHours(0, 0, 0, 0); return result;
}
export function dayString(date: Date): string { return `${pad(date.getUTCFullYear(),4)}-${pad(date.getUTCMonth()+1)}-${pad(date.getUTCDate())}`; }
export function parseDay(value: string): Date { const [y,m,d]=value.split('-').map(Number); return dateAt(y,m,d); }
export function todayString(): string { const d=new Date(); return `${pad(d.getFullYear(),4)}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
export function shiftDay(day: string, amount: number): string { const d=parseDay(day); d.setUTCDate(d.getUTCDate()+amount); return dayString(d); }
export function weekStart(day: string): string { return shiftDay(day,-((parseDay(day).getUTCDay()+6)%7)); }
export function validValue(value: string, unit: DateUnit): boolean {
  const year=Number(value.slice(0,4)); if(year<1||year>9999) return false;
  if(unit==='year') return /^\d{4}$/.test(value);
  if(unit==='month') return /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
  if(unit==='quarter') return /^\d{4}-Q[1-4]$/.test(value);
  return /^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(value)&&dayString(parseDay(value))===value;
}
export function periodBounds(value: string, unit: DateUnit): readonly [string,string] {
  const y=Number(value.slice(0,4));
  if(unit==='week'){ const start=weekStart(value);return [start,shiftDay(start,6)]; }
  if(unit==='date')return [value,value];
  const m=unit==='year'?1:unit==='quarter'?(Number(value.slice(-1))-1)*3+1:Number(value.slice(5,7));
  const months=unit==='year'?12:unit==='quarter'?3:1;
  return [dayString(dateAt(y,m)),dayString(dateAt(y,m+months,0))];
}
export function valueForDay(day: string, unit: DateUnit): string {
  if(unit==='week')return weekStart(day);
  if(unit==='month')return day.slice(0,7);
  if(unit==='year')return day.slice(0,4);
  if(unit==='quarter')return `${day.slice(0,4)}-Q${Math.ceil(Number(day.slice(5,7))/3)}`;
  return day;
}
export function cellsFor(year: number, month: number): string[] {
  const first=dayString(dateAt(year,month));const offset=(parseDay(first).getUTCDay()+6)%7;
  return Array.from({length:42},(_,i)=>shiftDay(first,i-offset));
}
export function labelFor(value: string, unit: DateUnit): string {
  const year=Number(value.slice(0,4)); if(unit==='year')return `${year}年`;
  if(unit==='month')return `${year}年${Number(value.slice(5,7))}月`;
  if(unit==='quarter')return `${year}年第${value.slice(-1)}季度`;
  const d=parseDay(value);return `${year}年${d.getUTCMonth()+1}月${d.getUTCDate()}日`;
}
export function createAvailability({min,max,disabledDate}:DateLimits) {
  const lower=min&&validValue(min,'date')?min:'0001-01-01'; const upper=max&&validValue(max,'date')?max:'9999-12-31';
  const cache=new Map<string,boolean>();const day=(value:string)=>validValue(value,'date')&&value>=lower&&value<=upper&&!disabledDate?.(value);
  function span(start:string,end:string, all:boolean):boolean {
    if(start>end||lower>upper)return false;
    if(all&&(start<lower||end>upper))return false;
    start=start<lower?lower:start;end=end>upper?upper:end;if(start>end)return false;
    if(!disabledDate)return true;
    for(let cursor=start;cursor<=end;cursor=shiftDay(cursor,1)){ const allowed=day(cursor);if(all&&!allowed)return false;if(!all&&allowed)return true;if(cursor===end)break; }
    return all;
  }
  function period(value:string,unit:DateUnit):boolean {
    const key=unit+value;const existing=cache.get(key);if(existing!==undefined)return existing;
    const bounds=validValue(value,unit)?periodBounds(value,unit):null;
    const result=bounds!==null&&span(bounds[0],bounds[1],unit==='date'||unit==='week');cache.set(key,result);return result;
  }
  function range(values:readonly [string,string],unit:DateUnit):boolean {
    if(!values.every(value=>validValue(value,unit)))return false;
    const sorted=[...values].sort();return span(periodBounds(sorted[0],unit)[0],periodBounds(sorted[1],unit)[1],true);
  }
  return {day,period,range};
}

export function validTime(value: string): boolean { return /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(value); }
export function validDateTime(value: string): boolean { return value.length===19&&validValue(value.slice(0,10),'date')&&value[10]===' '&&validTime(value.slice(11)); }
